import React, { useState } from "react"
import TemplateCreationDialog from "@/pages/Admin/Templates/components/TemplateCreationDialog"
import TemplateCard from "@/pages/Admin/Templates/components/TemplateCard"
import {
	DetailedWABAChannelMessageTemplate,
	WABATemplateQuality,
	WABATemplateStatus,
	WABATemplateCategory
} from "@/@integrations/WABA/protocols/wabaChannelMessageTemplate"
import {
	Grid,
	Typography,
	Button,
	TextField,
	InputAdornment,
	IconButton,
	TablePagination,
	Select, MenuItem,
	Divider as MuiDivider,
	Tooltip,
	Container,
	Link,
	Chip
} from "@material-ui/core"
import {
	Add,
	Sync,
	Search as SearchIcon,
	Close as CloseIcon
} from "@material-ui/icons"
import { Breadcrumb, Loading, Notification, Portlet } from "@/components"
import WabaTemplateGroupPermissionGate from "@/components/PermissionGate/WabaTemplateGroupPermissionGate"
import { getRowsLabel } from "@/utils/table"

import WABAChannelMessageTemplateService from "@/@integrations/WABA/services/WABAChannelMessageTemplate"

import { ErrorType } from "@/hooks/useValidation"
import ErrorHandlerService from "@/services/ErrorHandler"
import AnalyticsService from "@/services/Analytics"

import { useGlobalStateStore } from "@/store/GlobalState"
import useDidMount from "@/hooks/useDidMount"
import useStyles from "@/pages/Admin/Templates/styles"
import useDebounce from "@/hooks/useDebounce"
import undrawFileSearching from "@/assets/images/waba/undraw_file_searching.svg"
import TemplatePageSkeleton from "@/skeletons/Admin/TemplatePageSkeleton"
import { colors } from "@/styles/colors"

export type TemplatesWhereData = {
	page: number
	rowsPerPage: number
	count: number
}

export type TemplatesFiltersData = {
	search?: string
	status?: WABATemplateStatus
	quality?: WABATemplateQuality
	category?: WABATemplateCategory
}

const CATEGORY_FILTER_OPTIONS: WABATemplateCategory[] = ["marketing", "utility"]

export const TEMPLATE_CATEGORY_TO_READABLE_CATEGORY: Record<WABATemplateCategory, { label: string }> = {
	authentication: {
		label: "Autenticação"
	},
	marketing: {
		label: "Marketing"
	},
	utility: {
		label: "Utilidade"
	},
	otp: {
		label: "OTP"
	}
}

export const TEMPLATE_STATUS_TO_READABLE_STATUS: Record<WABATemplateStatus, { label: string, description?: string, color: string }> = {
	approved: {
		label: "Aprovado",
		color: colors.green[200]
	},
	deactivated: {
		label: "Desativado",
		color: colors.gray[200]
	},
	failed: {
		label: "Erro",
		color: colors.red[100]
	},
	paused: {
		label: "Pausado",
		description: "Pausado devido à baixa qualidade. Pode ser editado para melhorias. Recomenda-se seguir as políticas de criação de templates para evitar penalidades.",
		color: colors.gray[200]
	},
	rejected: {
		label: "Rejeitado",
		color: colors.red[100]
	},
	pending: {
		label: "Em análise",
		description: "O processo de análise de templates pode levar até 24 horas.",
		color: colors.yellow[300]
	},
	submitted: {
		label: "Enviado",
		description: "O processo de análise de templates pode levar até 24 horas.",
		color: colors.yellow[300]
	},
	unknown: {
		label: "Desconecido",
		description: "O status do template é atualizado com base no feedback do WhatsApp e pode levar algum tempo para refletir as mudanças.",
		color: colors.gray[200]
	}
}

export const TEMPLATE_QUALITY_TO_READABLE_QUALITY: Record<WABATemplateQuality, { label: string, description?: string, color: string }> = {
	high: {
		label: "Qualidade alta",
		color: colors.green[200]
	},
	medium: {
		label: "Qualidade média",
		color: colors.yellow[300]
	},
	low: {
		label: "Qualidade baixa",
		description: "O uso contínuo de templates de baixa qualidade pode levar à pausa, desabilitação e impactar a qualidade do seu número. Recomenda-se seguir as políticas de criação de templates para evitar penalidades.",
		color: colors.red[100]
	},
	unknown: {
		label: "Não qualificado",
		description: "A qualificação do template é ajustada com base no uso e no feedback dos seus clientes.",
		color: colors.gray[200]
	}
}

const Templates: React.FC = () => {
	const [templates, setTemplates] = useState<DetailedWABAChannelMessageTemplate[]>([])
	const [loading, setLoading] = useState<boolean>(true)
	const [whereData, setWhereData] = useState<TemplatesWhereData>({
		page: 0,
		rowsPerPage: 6,
		count: 0
	} as TemplatesWhereData)
	const [filters, setFilters] = useState<TemplatesFiltersData>({} as TemplatesFiltersData)
	const [isTemplateCreationDialogOpen, setIsTemplateCreationDialogOpen] = useState(false)

	const isSelectedFilterNotEmpty = Object.values(filters).some(value => value !== "")

	const classes = useStyles()
	const globalStateStore = useGlobalStateStore()
	const inboxChannelId = globalStateStore.currentChannel?.id
	const isChannelConnected = globalStateStore.currentChannel?.status === "connected"

	const getWABATemplates = async (newWhereData?: Partial<TemplatesWhereData>): Promise<void> => {
		try {
			setLoading(true)

			const searchConfig = { ...whereData, ...newWhereData, ...filters }
			const response = await WABAChannelMessageTemplateService.retrieveAllDetailedByInboxChannelId(searchConfig, inboxChannelId)

			setTemplates(response.rows)

			setWhereData(lastState => ({
				...lastState,
				count: response.count
			}))
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}
		setLoading(false)
	}

	const syncAllTemplates = async (): Promise<void> => {
		setLoading(true)
		try {
			await WABAChannelMessageTemplateService.syncAllTemplates(inboxChannelId)
			setWhereData(lastState => ({ ...lastState, page: 0 }))
			await getWABATemplates({ page: 0 })
		} catch (error) {
			Notification.error({
				message: "Não foi possivel sincronizar os templates com a Gupshup, por favor verifique sua conexão"
			})
			ErrorHandlerService.handle(error as ErrorType)
		}

		setLoading(false)
	}

	const handleWhereDataChange = async (key: keyof Omit<TemplatesWhereData, "count">, value: number | string) => {
		setWhereData(lastState => ({
			...lastState,
			[key]: value
		}))
		await getWABATemplates({ [key]: value })
	}

	const handleFiltersChange = (key: keyof TemplatesFiltersData, value: string) => {
		setFilters(prevFilters => ({
			...prevFilters,
			[key]: value
		}))
	}

	const handleRemoveDeleteTemplate = (templateId: number): void => {
		setTemplates(templates.filter((template) => template.id !== templateId))
	}

	const handleClearFilters = () => {
		setFilters({})
	}

	const handleAddTemplate = () => {
		setIsTemplateCreationDialogOpen(true)
	}

	useDidMount(() => {
		AnalyticsService.capture("waba_templates_opened")
		getWABATemplates()
	})

	useDebounce(
		async () => getWABATemplates(),
		filters,
		1250)

	return (
		<>
			{templates.length > 0 || filters
				? <Grid container direction="column" spacing={2}>
					<Grid item>
						<Breadcrumb data={[
							{ name: "Templates", pathname: "/admin/templates" }
						]} />
					</Grid>
					<Grid item>
						<Grid container justifyContent="space-between" alignItems="center">
							<Grid item xs={12}>
								<Grid container justifyContent="space-between" alignItems="center">
									<Grid item>
										<Typography variant="h5" color="textPrimary" className={classes.titleText}>
										Templates
										</Typography>
									</Grid>
									<Grid item>
										<Tooltip title={isChannelConnected ? "" : "Conecte seu canal Gupshup para criar templates"}>
											<span>
												<WabaTemplateGroupPermissionGate action="create">
													<Button variant="contained" color="primary" startIcon={<Add />} onClick={handleAddTemplate} disabled={!isChannelConnected}>
													Adicionar Template
													</Button>
												</WabaTemplateGroupPermissionGate>
											</span>
										</Tooltip>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>

					<Grid item>
						<Portlet elevation={1}>
							<Grid container direction="column" style={{ gap: 10 }}>
								<Grid item style={{ display: "contents" }}>
									<Grid container direction="row" justifyContent="space-between">
										<Grid item style={{ width: 338 }}>
											<TextField
												style={{ width: "100%" }}
												value={filters.search}
												placeholder="Pesquisar por nome ou conteúdo"
												variant="outlined"
												className={classes.search}
												onChange={({ target }) => handleFiltersChange("search", target.value)}
												InputProps={{
													startAdornment: (
														<InputAdornment position="start">
															<SearchIcon />
														</InputAdornment>
													),
													endAdornment: filters.search && (
														<IconButton size="small" onClick={() => handleFiltersChange("search", "")}>
															<CloseIcon fontSize="small" />
														</IconButton>
													)
												}}
												size="small"
											/>
										</Grid>
										<Grid item>
											<Grid container spacing={1}>
												<Grid item>
													<Select
														value={filters.category}
														onChange={({ target }) => handleFiltersChange("category", target.value as string)}
														variant="outlined"
														displayEmpty
														renderValue={() => filters.category ? TEMPLATE_CATEGORY_TO_READABLE_CATEGORY[filters.category].label : "Selecione a categoria"}
														className={classes.select}
													>
														<MenuItem value="">Selecione a categoria</MenuItem>
														{CATEGORY_FILTER_OPTIONS.map((key) => (
															<MenuItem key={key} value={key}>
																{TEMPLATE_CATEGORY_TO_READABLE_CATEGORY[key as WABATemplateCategory].label}
															</MenuItem>
														))}
													</Select>
												</Grid>
												<Grid item>
													<Select
														value={filters.status}
														onChange={({ target }) => handleFiltersChange("status", target.value as string)}
														variant="outlined"
														displayEmpty
														renderValue={() => filters.status ? TEMPLATE_STATUS_TO_READABLE_STATUS[filters.status].label : "Selecione o status"}
														className={classes.select}
													>
														<MenuItem value="">Selecione o status</MenuItem>
														{Object.keys(TEMPLATE_STATUS_TO_READABLE_STATUS).map((key) => (
															<MenuItem key={key} value={key}>
																{TEMPLATE_STATUS_TO_READABLE_STATUS[key as WABATemplateStatus].label}
															</MenuItem>
														))}
													</Select>
												</Grid>
												<Grid item>
													<Select
														value={filters.quality}
														onChange={({ target }) => handleFiltersChange("quality", target.value as string)}
														variant="outlined"
														displayEmpty
														renderValue={() => filters.quality ? TEMPLATE_QUALITY_TO_READABLE_QUALITY[filters.quality].label : "Selecione a qualidade"}
														className={classes.select}
													>
														<MenuItem value="">Selecione a qualidade</MenuItem>
														{Object.keys(TEMPLATE_QUALITY_TO_READABLE_QUALITY).map((key) => (
															<MenuItem key={key} value={key}>
																{TEMPLATE_QUALITY_TO_READABLE_QUALITY[key as WABATemplateQuality].label}
															</MenuItem>
														))}
													</Select>
												</Grid>
												<Grid item>
													<Tooltip title="Sincronizar com os templates da GupShup">
														<Button variant="contained" color="primary" onClick={async () => await syncAllTemplates()} className={classes.syncButton}>
															<Sync />
														</Button>
													</Tooltip>
												</Grid>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
								<Grid item>
									{isSelectedFilterNotEmpty && <Grid item>
										<Grid container alignItems="center" spacing={1}>
											<Grid item xs>
												<Chip
													style={{ padding: 0 }}
													label={"Remover filtro"}
													onDelete={handleClearFilters}
												/>
											</Grid>
										</Grid>
									</Grid>}
								</Grid>
								<Grid item>
									<Grid container direction="row" wrap="wrap" style={{ gap: 13 }}>
										<Loading
											loading={loading}
											customLoadingElement={<TemplatePageSkeleton />}
										>
											{templates.length > 0 ? templates.map((template) => (
												<TemplateCard
													onDelete={handleRemoveDeleteTemplate}
													key={template.name}
													{...template}
												/>
											)) : <Grid container justifyContent="center" style={{ padding: "20px 0px" }}>
												<Grid item>
													<Typography>Não encontramos nenhum templates com os filtros selecionados</Typography>
												</Grid>
											</Grid>}
										</Loading>
									</Grid>
								</Grid>
								<Grid item>
									<MuiDivider/>
									<TablePagination
										rowsPerPageOptions={[6, 12, 24, 48]}
										component="div"
										count={whereData?.count}
										rowsPerPage={whereData.rowsPerPage}
										page={whereData.page}
										onPageChange={(_, page) => handleWhereDataChange("page", page)}
										onRowsPerPageChange={({ target }) => handleWhereDataChange("rowsPerPage", +target.value)}
										labelRowsPerPage={"Resultados por página:"}
										labelDisplayedRows={tableData => getRowsLabel(tableData, whereData.rowsPerPage)}
									/>
								</Grid>
							</Grid>

						</Portlet>
					</Grid>
				</Grid>
				: <Container maxWidth="md">
					<Grid
						container
						alignItems="center"
						justifyContent="space-between"
						direction="column"
						spacing={4}
					>
						<Grid item>
							<Grid container justifyContent="center">
								<img
									src={undrawFileSearching}
									alt="Sem templates"
									className={classes.noTemplateImage}
								/>
							</Grid>
						</Grid>

						<Grid item xs={10}>
							<Typography
								variant="h5"
								className={classes.noTemplateTitle}
								align="center"
							>
								Crie seu primeiro template pela Letalk
							</Typography>
						</Grid>

						<Grid xs={8}>
							<Typography
								variant="body1"
								className={classes.noTemplateText}
								align="center"
							>
								Use templates para enviar mensagens personalizadas em escala para centenas de contatos! Inicie conversas diretas e automáticas com clientes no WhatsApp, aumentando as chances de visualizações e conversões.
							</Typography>
						</Grid>

						<Grid item>
							<Link
								href={""}
								underline="always"
								target="_blank"
								color="inherit"
							>
								Como criar o seu primeiro template utilizando a Letalk
							</Link>
						</Grid>

						<Grid item>
							<Tooltip title={isChannelConnected ? "" : "Conecte seu canal Gupshup para criar templates"}>
								<span>
									<WabaTemplateGroupPermissionGate action="create">
										<Button variant="contained" color="primary" startIcon={<Add />} onClick={handleAddTemplate} disabled={!isChannelConnected}>
											ADICIONAR TEMPLATE
										</Button>
									</WabaTemplateGroupPermissionGate>
								</span>
							</Tooltip>
						</Grid>
					</Grid>
				</Container>
			}

			<TemplateCreationDialog
				isOpen={isTemplateCreationDialogOpen}
				onClose={() => setIsTemplateCreationDialogOpen(false)}
				userName={globalStateStore.user.name}
				userEmail={globalStateStore.user.email}
				userPhone={globalStateStore.currentChannel?.extraData?.user?.phoneNumber}
			/>
		</>
	)
}

export default Templates
